import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Chip } from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenWordModal from "../../components/ForbiddenWordModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const items = action.payload;
    const newItems = [];
    items.forEach(item => {
      const idx = state.findIndex(s => s.id === item.id);
      if (idx !== -1) {
        state[idx] = item;
      } else {
        newItems.push(item);
      }
    });
    return [...state, ...newItems];
  }

  if (action.type === "UPDATE") {
    const item = action.payload;
    const idx = state.findIndex(s => s.id === item.id);
    if (idx !== -1) {
      state[idx] = item;
      return [...state];
    }
    return [item, ...state];
  }

  if (action.type === "DELETE") {
    const id = action.payload;
    const idx = state.findIndex(s => s.id === id);
    if (idx !== -1) {
      state.splice(idx, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const ForbiddenWords = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [words, dispatch] = useReducer(reducer, []);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchWords = useCallback(async () => {
    try {
      const { data } = await api.get("/forbidden-words", {
        params: { searchParam, pageNumber }
      });
      dispatch({ type: "LOAD", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchWords();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchWords]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    socket.on(`company-${user.companyId}-forbiddenword`, data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user]);

  const handleOpenModal = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelected(null);
    setModalOpen(false);
  };

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEdit = word => {
    setSelected(word);
    setModalOpen(true);
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/forbidden-words/${id}`);
      toast.success(i18n.t("forbiddenWords.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchWords();
  };

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("forbiddenWords.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deleting.id)}
      >
        {i18n.t("forbiddenWords.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ForbiddenWordModal
        open={modalOpen}
        onClose={handleCloseModal}
        reload={fetchWords}
        forbiddenWordId={selected && selected.id}
      />
      <MainHeader>
        <Title>{i18n.t("forbiddenWords.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("forbiddenWords.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" color="primary" onClick={handleOpenModal}>
            {i18n.t("common.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("forbiddenWords.table.term")}</TableCell>
              <TableCell align="center">{i18n.t("forbiddenWords.table.replacement")}</TableCell>
              <TableCell align="center">{i18n.t("common.status")}</TableCell>
              <TableCell align="center">{i18n.t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {words.map(word => (
                <TableRow key={word.id}>
                  <TableCell align="center">{word.term}</TableCell>
                  <TableCell align="center">{word.replacement || "—"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={word.active ? i18n.t("common.active") : i18n.t("common.inactive")}
                      color={word.active ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(word)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeleting(word);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ForbiddenWords;
